import { useCallback, useState } from 'react';
import { TableMeta } from '../types';

/**
 *
 * Small, ephemeral store for table-level metadata produced by data fetches
 * Lives inside the `TableProvider` and is exposed via context so *controllers* (like
 * PrioritizeProvider) and *in-table* components can react to server facts.
 *
 * Scope & lifecycle
 * - **Owned by TableProvider**; reset when the provider unmounts.
 * - **Not persisted** (no localStorage/URL). Intended for per-session UI logic.
 * - **Updated by BaseTable** (e.g., after a successful fetch).
 *
 * @returns {
 *   tableMeta: current metadata or null;
 *   updateTableMeta(next): void; // safely updates with idempotent compare
 * }
 *
 */

export const useTableMeta = () => {
	const [tableMeta, setTableMeta] = useState<TableMeta | null>(null);

	const updateTableMeta = useCallback(
		(next: typeof tableMeta | ((p: typeof tableMeta) => typeof tableMeta)) => {
			setTableMeta((prev) => {
				const computed = typeof next === 'function' ? next(prev) : next;
				const same =
					prev !== null &&
					computed !== null &&
					prev.current_page === computed.current_page &&
					prev.total_pages === computed.total_pages &&
					prev.total_rows === computed.total_rows;
				return same ? prev : computed;
			});
		},
		[],
	);

	return { tableMeta, updateTableMeta };
};
